/**
 * «Інфо» — опис платформи Helios EMS та умови її використання.
 *
 * ОКРЕМА СТОРІНКА (/about), а не переробка чогось наявного. Пункт меню «Інфо»
 * раніше вів на «Ринок УЕБ»; той розділ лишається робочим за своєю адресою
 * /market/ueex — просто більше не висить у меню. Стара інформація прихована,
 * але не видалена.
 *
 * Адреса саме /about, а не /info: /info вже зайнятий розділом новин
 * (Інфоаналітика), і другий маршрут із тим самим коренем плутав би.
 *
 * ВЕРСТКА. Опис платформи зібраний в одну плитку: заголовок, ринковий
 * контекст і два абзаци вступу — це один текст, і читатись він має суцільно.
 * Раніше шапка лежала на фоні сторінки, а абзаци йшли під нею окремо, через
 * що вступ розпадався на дві незв'язані частини.
 *
 * Текст іде на всю ширину плитки: обмеження довжини рядка пробували, але
 * у плитці воно читалось як порожня друга колонка, тобто як поділ картки.
 *
 * Догма кольору: «Важливо» — це застереження, тож бурштин (--warn), НЕ
 * червоний: реальної помилки тут немає.
 *
 * Тексти українською просто в коді, без i18n: це юридичні формулювання про
 * права на платформу, і їх переклад мав би окремо узгоджуватись юристом, а не
 * робитись принагідно. Той самий підхід на сусідніх сторінках.
 *
 * ФОРМАТУВАННЯ: у вихідному документі частина переліків іде суцільним рядком
 * через крапку з комою («…необхідному для: функціонування платформи;
 * забезпечення…»). Тут вони розкладені в марковані списки — зміст той самий,
 * читається значно краще.
 */
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useState, type CSSProperties } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { getStoredUser } from '@/hooks/useAuth';
import {
  AlertTriangle, Ban, Database, KeyRound, Scale, type LucideIcon,
} from 'lucide-react';
import { hasArtemKView } from '@/utils/artemkView';

interface Section {
  id: string;
  title: string;
  Icon: LucideIcon;
  /** Короткий підпис під назвою — щоб зрозуміти суть, не розгортаючи. */
  summary: string;
  /** Застережливий блок фарбується бурштином (догма: увага, не помилка). */
  warn?: boolean;
  paragraphs?: string[];
  leadIn?: string;
  bullets?: string[];
  tail?: string[];
}

const getLead = () => i18n.t('ak_about_helios_ems_is_a_software_platform_for');

const getIntro = (): string[] => [
  i18n.t('ak_about_the_platform_is_an_in_house_developmen'),
  i18n.t('ak_about_the_functionality_of_the_platform_the'),
];

/**
 * Ринковий контекст винесений окремим рядком: у документі це був перелік
 * через кому всередині довгого абзацу, і він там губився.
 */
const getMarketContext = () => [i18n.t('ak_about_dam_and_idm'), i18n.t('ak_about_mms_of_npc_ukrenergo'), 'DataHub', i18n.t('ak_about_commands_of_the_tso_dso'), i18n.t('ak_about_neurc_requirements')];

const getSections = (): Section[] => [
  {
    id: 'rights',
    title: i18n.t('ak_about_rights_of_use'),
    Icon: Scale,
    summary: i18n.t('ak_about_who_owns_the_platform_and_what_exactly'),
    paragraphs: [
      i18n.t('ak_about_helios_ems_including_the_software_code'),
      i18n.t('ak_about_the_user_is_granted_a_limited_non_excl'),
      i18n.t('ak_about_granting_access_to_the_platform_does_n'),
      i18n.t('ak_about_the_exclusive_property_rights_to_the_s'),
    ],
  },
  {
    id: 'data',
    title: i18n.t('ak_about_user_and_facility_data'),
    Icon: Database,
    summary: i18n.t('ak_about_whose_data_the_platform_processes_and'),
    paragraphs: [
      i18n.t('ak_about_data_on_the_facility_its_equipment_ope'),
    ],
    leadIn: i18n.t('ak_about_within_the_scope_and_for_the_term_of_t'),
    bullets: [
      i18n.t('ak_about_operation_of_the_platform'),
      i18n.t('ak_about_providing_the_functionality_envisaged'),
      i18n.t('ak_about_monitoring_and_analysis_of_the_facilit'),
      i18n.t('ak_about_producing_forecasts_reports_and_other'),
      i18n.t('ak_about_technical_support_and_maintenance'),
      i18n.t('ak_about_performing_other_obligations'),
    ],
    tail: [
      i18n.t('ak_about_the_user_is_responsible_for_having_pro'),
    ],
  },
  {
    id: 'access',
    title: i18n.t('ak_about_granting_and_using_access'),
    Icon: KeyRound,
    summary: i18n.t('ak_about_roles_accounts_and_responsibility_for'),
    paragraphs: [
      i18n.t('ak_about_the_user_independently_determines_the'),
    ],
    leadIn: i18n.t('ak_about_the_user_is_responsible_for'),
    bullets: [
      i18n.t('ak_about_the_accuracy_of_the_data_and_informati'),
      i18n.t('ak_about_safekeeping_of_the_credentials_of_its'),
      i18n.t('ak_about_preventing_the_transfer_of_credentials'),
      i18n.t('ak_about_compliance_by_authorized_persons_with'),
      i18n.t('ak_about_actions_performed_by_such_persons_usin'),
    ],
    tail: [
      i18n.t('ak_about_transferring_the_right_to_use_the_plat'),
    ],
  },
  {
    id: 'limits',
    title: i18n.t('ak_about_restrictions_on_use'),
    Icon: Ban,
    summary: i18n.t('ak_about_what_is_prohibited_without_the_written'),
    leadIn: i18n.t('ak_about_without_the_prior_written_consent_of_h'),
    bullets: [
      i18n.t('ak_about_to_reproduce_copy_decompile_disassembl'),
      i18n.t('ak_about_to_modify_the_software_code_algorithms'),
      i18n.t('ak_about_to_transfer_assign_sublicense_lease_or'),
      i18n.t('ak_about_to_create_derivative_software_products'),
      i18n.t('ak_about_to_remove_conceal_or_alter_the_rights'),
      i18n.t('ak_about_to_take_any_other_actions_aimed_at_cir'),
    ],
  },
  {
    id: 'important',
    title: i18n.t('ak_about_important'),
    Icon: AlertTriangle,
    summary: i18n.t('ak_about_limits_of_liability_for_calculations_a'),
    warn: true,
    paragraphs: [
      i18n.t('ak_about_the_information_calculations_forecasts'),
      i18n.t('ak_about_the_data_and_results_generated_by_the'),
      i18n.t('ak_about_the_user_is_solely_responsible_for_dec'),
    ],
  },
];

/**
 * Кегль сторінки. Для ArtemK (прохання 09.10) усі написи приблизно на чверть
 * менші за звичайні — один множник на сторінку. Рахується у виклику, а не на
 * рівні модуля: бандл один, і під час його завантаження користувач ще не
 * залогінений.
 */
const DENSE_SCALE = 0.75;
const fs = (n: number) =>
  hasArtemKView(getStoredUser()?.login) ? Math.round(n * DENSE_SCALE * 2) / 2 : n;

const body = (): CSSProperties => ({
  // 14px замість 12.5 і міжрядковий 1.75: це суцільний юридичний текст,
  // який читають рядок за рядком, а не зчитують поглядом, як показники.
  // Колір --text замість --text-muted: приглушений сірий доречний для
  // підписів, але на абзаці в кілька рядків він стомлює око.
  fontSize: fs(14), lineHeight: 1.75, color: 'var(--text)', margin: 0,
});

function Paragraphs({ items }: { items: string[] }) {
  return <>{items.map((p, i) => <p key={i} style={body()}>{p}</p>)}</>;
}

/**
 * Рядок акордеона в стилі сайту heliosaggregate.com: плаский список без
 * плиток, іконка акцентом ліворуч, назва, «+» праворуч, рядки розділені
 * тонкою лінією.
 *
 * Плитки тут свідомо НЕ використовуються: п'ять коробок поспіль дробили
 * сторінку на блоки, хоча це один суцільний документ. Лінія-роздільник
 * тримає його цілим, а глибина сторінці не потрібна — це текст, не дані.
 */
function SectionRow({ section, open, onToggle }: {
  section: Section; open: boolean; onToggle: () => void;
}) {
  // Іконки монохромні: у списку однорідних розділів колір нічого не
  // розрізняє, лише шумить. Виняток — «Важливо»: бурштин тут несе сенс
  // застереження, а не прикрасу.
  const accent = section.warn ? 'var(--warn)' : 'var(--text-muted)';
  // «+» білий на всіх рядках — це керуючий елемент, однаковий скрізь.
  const plus = 'var(--text)';
  return (
    <div style={{ borderBottom: '1px solid var(--line)' }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full flex items-center cursor-pointer group"
        style={{ gap: 16, padding: '17px 4px', background: 'transparent', border: 'none', textAlign: 'left' }}
      >
        <section.Icon size={19} strokeWidth={1.7} style={{ color: accent, flexShrink: 0 }} />

        <span style={{ flex: 1, minWidth: 0 }}>
          <span className="font-display" style={{
            display: 'block', fontSize: fs(17), fontWeight: 600, color: 'var(--text)', lineHeight: 1.3,
          }}>
            {section.title}
          </span>
          {/* Підпис дає суть розділу, не розгортаючи його. */}
          <span style={{ display: 'block', fontSize: fs(13), color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.45 }}>
            {section.summary}
          </span>
        </span>

        {/* «+» / «−» — дві риски, з яких вертикальна ховається у відкритому
            стані. Обертання дало б хрестик, а він читається як «закрити». */}
        <span style={{
          position: 'relative', width: 16, height: 16, flexShrink: 0,
        }} aria-hidden="true">
          <span style={{
            position: 'absolute', top: 7.5, left: 0, width: 16, height: 1.5,
            background: plus, borderRadius: 1,
          }} />
          <span style={{
            position: 'absolute', left: 7.5, top: 0, width: 1.5, height: 16,
            background: plus, borderRadius: 1,
            transform: open ? 'scaleY(0)' : 'scaleY(1)',
            transition: 'transform .18s ease',
          }} />
        </span>
      </button>

      {open && (
        <div style={{ display: 'grid', gap: 13, padding: '2px 4px 22px 39px' }}>
          {section.paragraphs && <Paragraphs items={section.paragraphs} />}
          {section.leadIn && <p style={{ ...body(), color: 'var(--text)' }}>{section.leadIn}</p>}
          {section.bullets && (
            <ul style={{ display: 'grid', gap: 7, margin: 0, padding: 0, listStyle: 'none' }}>
              {section.bullets.map((b, i) => (
                <li key={i} className="flex items-start" style={{ gap: 9 }}>
                  <span style={{
                    width: 4, height: 4, borderRadius: 999, background: accent,
                    flexShrink: 0, marginTop: 10,
                  }} />
                  <span style={{ ...body(), flex: 1 }}>{b}</span>
                </li>
              ))}
            </ul>
          )}
          {section.tail && <Paragraphs items={section.tail} />}
        </div>
      )}
    </div>
  );
}

export default function AboutPage() {
  const { t } = useTranslation();
  // Усі блоки згорнуті на старті, як у вихідному документі: спершу коротка
  // суть, деталі — за бажанням. Відкривати можна кілька одночасно.
  const [open, setOpen] = useState<string[]>([]);
  const toggle = (id: string) =>
    setOpen((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const allOpen = open.length === getSections().length;

  return (
    <DashboardLayout breadcrumb={[t('ak_about_info')]}>
      <div className="min-h-screen -m-4 md:-m-6" style={{ background: 'var(--bg)' }}>
        {/* Без обмеження ширини: верхній блок і список розділів займають
            усю сторінку. */}
        <div className="px-4 md:px-6 pt-6 pb-12" style={{ fontFamily: 'Urbanist, Inter, sans-serif' }}>

          {/* Заголовки стоять НА ФОНІ сторінки, а не в плитках: плитка тоді
              тримає лише текст, а назви утворюють спільний ритм сторінки —
              одна вертикаль, на якій око знаходить і «Helios EMS», і «Умови
              використання». Усередині плиток вони конкурували з текстом за
              той самий верхній край. */}
          <h1 className="font-display" style={{
            fontSize: fs(26), fontWeight: 600, color: 'var(--text)', lineHeight: 1.15,
            margin: '0 0 10px',
          }}>
            Helios EMS
          </h1>

          <div className="tile p-5 md:p-6" style={{ marginBottom: 26 }}>
            {/* Увесь вступ одним кеглем (BODY, 14/1.75): лід, ринковий
                контекст і два абзаци — суцільний текст, а не рівні
                важливості. Лід був більшим, перелік приглушеним — разом це
                давало три різні розміри в одному абзаці й читалось рвано.
                Виділяється лише назва платформи заголовком.

                Ринковий контекст саме текстом, а не чіпами: пілюля в кабінеті
                означає мітку або фільтр, тобто обіцяє дію, якої тут немає. */}
            {/* Щільний блок: на всю ширину плитки кожен абзац вкладається
                приблизно в рядок, тож великі проміжки між ними лише розганяли
                картку вниз, хоча місця по горизонталі вистачає. Проміжок
                зменшено з 11 до 5 — абзаци лишаються окремими, але блок
                читається як суцільний текст. */}
            <div style={{ display: 'grid', gap: 5 }}>
              <p style={body()}>{getLead()}</p>
              <p style={body()}>{getMarketContext().join(' · ')}</p>
              <Paragraphs items={getIntro()} />
            </div>
          </div>

          {/* Заголовок другої плитки — теж на фоні, тієї ж ваги, що й перший.
              «Розгорнути все» їде з ним: кнопка керує всім списком, тож
              стоїть на рівні його назви, а не всередині. Лінії-роздільника
              тут уже не треба — межу задає сама плитка під заголовком. */}
          <div className="flex items-baseline justify-between" style={{ gap: 12, marginBottom: 10 }}>
            <h2 className="font-display" style={{
              fontSize: fs(26), fontWeight: 600, color: 'var(--text)', lineHeight: 1.15, margin: 0,
            }}>
              {t('ak_about_terms_of_use')}
            </h2>
            <button
              type="button"
              onClick={() => setOpen(allOpen ? [] : getSections().map((s) => s.id))}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                fontSize: fs(12.5), color: 'var(--text-muted)', padding: 0, whiteSpace: 'nowrap',
              }}
            >
              {allOpen ? t('ak_about_collapse_all') : t('ak_about_expand_all')}
            </button>
          </div>

          <div className="tile p-5 md:p-6">
            {getSections().map((sec) => (
              <SectionRow
                key={sec.id}
                section={sec}
                open={open.includes(sec.id)}
                onToggle={() => toggle(sec.id)}
              />
            ))}
          </div>

          <footer style={{ marginTop: 22, paddingTop: 12, borderTop: '1px solid var(--line)' }}>
            <span style={{ fontSize: fs(11), color: 'var(--text-dim)' }}>
              {t('ak_about_helios_aggregator_llc')} ·{' '}
              <a href="https://heliosaggregate.com" target="_blank" rel="noopener noreferrer"
                style={{ color: 'var(--text-muted)' }}>heliosaggregate.com</a>
            </span>
          </footer>

        </div>
      </div>
    </DashboardLayout>
  );
}
